/** 
 * © 2025 COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 'CONTPAQi'. Todos los derechos reservados. 
 * Este código fuente constituye una obra original protegida por la legislación nacional e internacional en materia de propiedad intelectual. 
 * Su reproducción, distribución, comunicación pública, transformación, descompilación, ingeniería inversa o cualquier uso no autorizado, 
 * total o parcial, se encuentra expresamente prohibido sin la previa autorización por escrito de COMPUTACIÓN EN ACCIÓN, S.A. DE C.V. 
 * El uso indebido de este software puede ser constitutivo de delitos y dará lugar a las responsabilidades civiles y penales correspondientes. 
*/ 
 
import { takeLatest, call, put, select  } from 'redux-saga/effects';
import * as LegalDocumentsActions from '../actions/LegalDocumentsActions';
import * as ConfigurationActions from '../actions/ConfigurationActions';
import { listLegalDocumentsCall, sendDocumentSignedCall } from '../../services/LegalDocumentsServices';
import { Typography, Modal } from 'antd';
import { getSigningKey } from '../../services/CryptoService'
import { encryptAES, generateRandomKey, rsaOAEP256Encrypt } from '../../utils/encrypt/encrypt'
import { AppMessage } from '../../AppMessages';

const KEYLENGTH = 16

const EVENT = {
    CLOSE_IFRAME: 'closeIframe',
    SHOW_PORTAL: 'showPortal',
    REDIRECT: 'redirect'
}

const Flow = {
    VALIDATE_DOCUMENTS_TO_SIGN: 'ValidateDocumentsToSign',
    LOGIN: 'Login',
    EXTERNAL_COMPANY: 'ExternalCompany',
    ACTIVATION: 'Activation'
}

async function encryptCertificate(certificate, privateKey, password){

    if (certificate !== null && privateKey !== null && password !== null){
        const authorization = await select(state => state.LegalDocumentsReducer.accessToken);
        const responseCrypto = await call(getSigningKey, authorization);
        if (responseCrypto.status === 200) {
            const signingKey = responseCrypto.data.base64Key;
            const randomKey = generateRandomKey(KEYLENGTH);
            let transportKey = await call(rsaOAEP256Encrypt, randomKey, signingKey);
            return {
                certificateEncrypt: await encryptAES(certificate, randomKey),
                privateKeyEncrypt: await encryptAES(privateKey, randomKey),
                passwordEncrypt: await encryptAES(password, randomKey),
                transportKey: transportKey
            }
        }
    }
}

function sendEventToParent(event){
    window.parent.postMessage(event, '*'); 
}

const continueFlowEvent = (flow) => {
    if (flow === Flow.VALIDATE_DOCUMENTS_TO_SIGN){
        sendEventToParent(EVENT.SHOW_PORTAL);
    }
    else if (flow === Flow.LOGIN){
        sendEventToParent(EVENT.CLOSE_IFRAME);
    }
    else if (flow === Flow.EXTERNAL_COMPANY){
        sendEventToParent(EVENT.REDIRECT);
    }
    else if (flow === Flow.ACTIVATION){
        sendEventToParent(EVENT.CLOSE_IFRAME);
    }
}

function getBase64(file, privateKey, certificate) {
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
        console.log('result',reader.result);
        if(file.type === 'fiel/certificado.key'){
            privateKey = reader.result;
        } else if(file.type === 'fiel/certificado.cer'){
            certificate = reader.result;
        }
    };
    reader.onerror = function (error) {
        console.log('Error: ', error);
    };
}

function* getListLegalDocuments(action) {
    let flow = '';
    try {
        // Headers: identityId, rfc, serviceId, Cache-Control
        // Params: documentTypeOwner*, limit*, skip, orderBy*nombre, sortDirection*ASC, status, version, roleId
        let status = action.status ? '&status=' + action.status : '&status=1';
        let orderBy = '&orderBy=Name';
        let sortDirection = '&sortDirection=ASC';

        const authorization = yield select(state => state.LegalDocumentsReducer.accessToken);
        const identityId = yield select(state => state.LegalDocumentsReducer.identityId);
        const documentTypeOwner = `documentTypeOwner=${yield select(state => state.LegalDocumentsReducer.documentTypeOwner)}`
        const role = yield select(state => state.LegalDocumentsReducer.role);
        const roleId = role ? `&roleId=${role}` : '';
        const rfc = yield select(state => state.LegalDocumentsReducer.rfc);
        const serviceId = yield select(state => state.LegalDocumentsReducer.serviceId);
        const transportKey = yield select(state => state.LegalDocumentsReducer.transportKey);
        const licenseId = yield select(state => state.LegalDocumentsReducer.licenseId);
        const platformDocumentType = yield select(state => state.LegalDocumentsReducer.platformDocumentType);
        const platformDocumentTypeId = platformDocumentType ? `&platformDocumentType=${platformDocumentType}` : '';
        flow = yield select(state => state.LegalDocumentsReducer.flow);
        
        const response = yield call(
            listLegalDocumentsCall, 
            documentTypeOwner, 
            status, 
            identityId, 
            roleId, 
            orderBy, 
            sortDirection, 
            rfc, 
            serviceId, 
            authorization, 
            transportKey, 
            licenseId,
            platformDocumentTypeId
        );
        if (response.status === 200) {
            const listLegalDocuments = response.data;
            if(listLegalDocuments?.length > 0) {
                listLegalDocuments.forEach(document => {
                    const documentName = document?.originalName || document?.name;
                    document.acceptance = undefined; // inicialización de la variable acceptance indefinida para primera validación dentro del componente 'Documents'
                    document.booleanSigned = false;
                    document.label = documentName + ' - ' + document?.version;
                });
                yield put({ type: LegalDocumentsActions.GET_LIST_DOCUMENTS_SUCCESS, listLegalDocuments });
            } else {
                throw new Error (AppMessage.EMPTY_DOCUMENTS);
            }
        } else{
            throw new Error (response.detail || AppMessage.EMPTY_DOCUMENTS);
        }
    } catch (error) {
        console.log(error);
        yield put({ type: LegalDocumentsActions.GET_LIST_DOCUMENTS_FAILURE });
        continueFlowEvent(flow);
    }
}

export function* getListLegalDocumentsSaga() {
    yield takeLatest(LegalDocumentsActions.GET_LIST_DOCUMENTS_REQUEST, getListLegalDocuments);
}

function* sendDocumentsSigned(action) {
    const { info } = Modal;
    let flow = '';
    try {
        //Headers: authorization*, rfc, certificate, privateKey, password, trasportKey
        let data = [];
        let certificate = null;
        let privateKey = null;
        let password = action.form.contraseña ? action.form.contraseña : null;

        const identityId = yield select(state => state.LegalDocumentsReducer.identityId);
        const licenseId = yield select(state => state.LegalDocumentsReducer.licenseId);
        const rfc = yield select(state => state.LegalDocumentsReducer.rfc);
        const secondaryTransportKey = yield select(state => state.LegalDocumentsReducer.transportKey);
        const authorization = yield select(state => state.LegalDocumentsReducer.accessToken);
        const application = yield select(state => state.LegalDocumentsReducer.serviceId);
        flow = yield select(state => state.LegalDocumentsReducer.flow);
        const role = yield select(state => state.LegalDocumentsReducer.role);
        const sourceTypeId = yield select(state => state.LegalDocumentsReducer.sourceTypeId);
        const user =  yield select(state => state.LegalDocumentsReducer.user);

        const rfcBase64 = rfc;

        let headers = { 'Content-Type': 'application/json', 'Authorization': authorization, 'transportKey': secondaryTransportKey};

        if (action.form.archivos)
            action.form.archivos.fileList.forEach(file => {
                getBase64(file.originFileObj, privateKey, certificate)
            });

        action.signedDocuments.forEach(document => {
            if (document.acceptance === undefined) return;
            let metadata = [
                {
                    "key": "serie",
                    "value": licenseId // Cuenta CONTPAQi
                }
            ];

            if(action.formInitialData){
                if(application)
                {
                    metadata.push({
                        "key": "application",
                        "value": application.trim()
                    })
                }
                if(role){ 
                    metadata.push({
                        "key": "rol",
                        "value": role.trim()
                    }) 
                }
                if(rfc){ 
                    metadata.push({
                        "key": "rfc",
                        "value": rfcBase64
                    }) 
                }
                if(user){ 
                    metadata.push({
                        "key": "user",
                        "value": user,
                    }) 
                }
            }

            let signSourceTypeId = undefined;

            if (flow === Flow.LOGIN) signSourceTypeId = 2;
            else if (flow === Flow.ACTIVATION) signSourceTypeId = 1;
            else signSourceTypeId = 3

            let singleDocument = {
                'documentId': document.internalDocumentId,
                'sourceTypeId': sourceTypeId,
                'acceptance': document.acceptance === 'accept',
                'signSourceTypeId': signSourceTypeId,
                'metadata': metadata
            };
            data.push(singleDocument);
        });

        if(certificate){ headers = {  ...headers,  'certificate': certificate,  } }
        if(privateKey){ headers = {  ...headers,  'privateKey': privateKey,  } }
        if(password){ headers = {  ...headers,  'password': password,  } }
        if(secondaryTransportKey){ headers = {  ...headers,  'secondaryTransportKey': secondaryTransportKey,  } }

        const objMessageResponse = {title: AppMessage.DOCUMENT_SIGN, textMessage: []}
        let banderaError = false;
        let responseDetail = ''
        let signedOk = []
        let response = null;

        for (let index = 0; index < data.length; index++) {
            response = yield call(sendDocumentSignedCall, data[index], headers);
            if (response.status === 201) {
                objMessageResponse.textMessage.push(action.signedDocuments[index].label); 
                signedOk.push(data[index]);
                
                yield put({ type: ConfigurationActions.CLEAN_FORM, cleanForm: 'signatureComponent' });
            } else{
                banderaError = true;
                objMessageResponse.textMessage.push(action.signedDocuments[index].label); 
                responseDetail = response.detail;

                yield put({ type: ConfigurationActions.CLEAN_FORM, cleanForm: 'signatureComponent' });
            } 
        }

        if(objMessageResponse.textMessage.length > 0){
            if(signedOk.length === data.length) {
                continueFlowEvent(flow);
            }
            else
            {
                yield put({ type: LegalDocumentsActions.SIGN_DOCUMENT_FAILURE });
                info({
                    title: AppMessage.LEGAL_DOCUMENTS_TITLE_ERROR,
                    centered: true,
                    content: <Typography.Text>
                        {banderaError ?  responseDetail : AppMessage.SIGNATURE_ERROR}
                    </Typography.Text>,
                    okText: 'Ok',
                    onOk() {
                        continueFlowEvent(flow);
                    },
                });
            }
        }  

    } catch (error) {
        console.log(error);
        yield put({ type: LegalDocumentsActions.SIGN_DOCUMENT_FAILURE });
        info({
            title: AppMessage.LEGAL_DOCUMENTS_TITLE_ERROR,
            centered: true,
            content: <Typography.Text>
                {AppMessage.SIGNATURE_ERROR}
            </Typography.Text>,
            okText: 'Ok',
            onOk() {
                continueFlowEvent(flow);
            },
        });
    }
}


export function* sendDocumentsSignedSaga() {
    yield takeLatest(LegalDocumentsActions.SIGN_DOCUMENT_REQUEST, sendDocumentsSigned);
}